import { ArrowsInSimpleIcon } from "@phosphor-icons/react/dist/csr/ArrowsInSimple";
import { ArrowsOutSimpleIcon } from "@phosphor-icons/react/dist/csr/ArrowsOutSimple";
import { type ReactNode, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { EDITOR_LIMITS } from "@/payroll/editor/constants";
import type { Composer } from "./use-composer";

export const ComposerInput = ({
  composer,
  busy,
  canSend,
  onSubmit,
  children,
}: {
  composer: Composer;
  busy: boolean;
  canSend: boolean;
  onSubmit: () => void;
  children: ReactNode;
}) => {
  const [expanded, setExpanded] = useState(false);
  const length = composer.text.length;
  const tooLong = length > EDITOR_LIMITS.messageLength;
  return (
    <Stack gap={2} className="min-w-0 p-3">
      <label htmlFor="payroll-change" className="sr-only">
        Describe your payroll change
      </label>
      <div
        className={cn(
          "relative min-h-10 min-w-0 overflow-hidden text-sm leading-6",
          {
            "max-h-[min(12rem,30cqh)]": !expanded,
            "max-h-[min(24rem,50cqh)] min-h-[min(16rem,40cqh)]": expanded,
          },
        )}
      >
        <span
          aria-hidden
          className="invisible block wrap-anywhere whitespace-pre-wrap"
        >{`${composer.text} `}</span>
        <textarea
          ref={composer.field}
          id="payroll-change"
          rows={1}
          value={composer.text}
          aria-invalid={tooLong || undefined}
          aria-describedby={tooLong ? "payroll-change-limit" : undefined}
          onChange={(event) => composer.setText(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              if (canSend) onSubmit();
            }
          }}
          placeholder={
            busy ? "Add another change…" : "Describe the payroll change…"
          }
          className="absolute inset-0 m-0 quiet-scrollbar h-full min-h-0 w-full resize-none overflow-y-auto border-0 bg-transparent p-0 leading-6 wrap-anywhere whitespace-pre-wrap outline-hidden placeholder:text-subtle"
        />
      </div>
      <Stack direction="row" gap={1} align="center">
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={expanded ? "Reduce input height" : "Expand input"}
          aria-expanded={expanded}
          title={expanded ? "Reduce input height" : "Expand input"}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <ArrowsInSimpleIcon aria-hidden />
          ) : (
            <ArrowsOutSimpleIcon aria-hidden />
          )}
        </Button>
        <span className="flex-1" />
        {length >= EDITOR_LIMITS.messageLength * 0.9 && (
          <span
            className={cn("mr-2 text-xs text-subtle tabular-nums", {
              "text-danger": tooLong,
            })}
          >
            {length.toLocaleString()} /{" "}
            {EDITOR_LIMITS.messageLength.toLocaleString()}
          </span>
        )}
        {children}
      </Stack>
      {tooLong && (
        <p
          id="payroll-change-limit"
          role="alert"
          className="text-xs text-danger"
        >
          This message is too long. Split it into smaller messages or attach a
          file. All pasted text is kept here.
        </p>
      )}
    </Stack>
  );
};
